import { useState, useEffect } from 'react';
import { FaDollarSign, FaPercentage, FaCube, FaTrophy, FaCopy, FaExternalLinkAlt } from 'react-icons/fa';
import config from '../../config';
import terms from '../../config/terminology';
import { formatCurrency } from '../../utils/formatCurrency';
import CastleVisualization from './CastleVisualization';

export default function MyCastleSection({ user }) {
  const [castleData, setCastleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weeklyNote, setWeeklyNote] = useState('');
  const [editingNote, setEditingNote] = useState(false);
  const [savingNote, setSavingNote] = useState(false);

  const token = localStorage.getItem('access_token');

  useEffect(() => {
    fetchCastleData();
  }, []);

  const fetchCastleData = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${config.API_URL}/creators/castle-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCastleData(data);
        setWeeklyNote(data.weekly_note || '');
      }
    } catch (err) {
      console.error('Error fetching castle data:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveWeeklyNote = async () => {
    setSavingNote(true);
    try {
      const res = await fetch(`${config.API_URL}/creators/weekly-note`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: weeklyNote.trim() || null })
      });
      if (res.ok) {
        setEditingNote(false);
        setCastleData(prev => ({ ...prev, weekly_note: weeklyNote.trim() || null }));
      }
    } catch (err) {
      console.error('Error saving weekly note:', err);
    } finally {
      setSavingNote(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const tierColor = (tier) => {
    switch (tier) {
      case 'title': return 'text-amber-400';
      case 'premium': return 'text-purple-400';
      default: return 'text-blue-400';
    }
  };

  const publicUrl = `${window.location.origin}/castle/${user?.username}`;

  if (loading && !castleData) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold">My Castle</h2>
          <p className="text-gray-500 text-sm">Build your castle brick by brick</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigator.clipboard.writeText(publicUrl)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm transition"
          >
            <FaCopy size={12} />
            <span>Share</span>
          </button>
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg text-sm font-semibold transition"
          >
            <FaExternalLinkAlt size={12} />
            <span>Public View</span>
          </a>
        </div>
      </div>

      {/* Weekly Note */}
      <div className="bg-[#1c1f2d] rounded-xl p-5 border border-gray-800/50 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">📝</span>
            <h3 className="font-semibold text-sm">Weekly Note</h3>
          </div>
          {!editingNote && (
            <button onClick={() => setEditingNote(true)} className="text-xs text-cyan-400 hover:text-cyan-300 transition">
              Edit
            </button>
          )}
        </div>
        {editingNote ? (
          <div>
            <textarea
              value={weeklyNote}
              onChange={(e) => setWeeklyNote(e.target.value)}
              placeholder="Share a message with your supporters..."
              maxLength={1000}
              rows={3}
              className="w-full px-3 py-2 bg-[#14161f] border border-gray-700 rounded-lg focus:border-cyan-500 focus:outline-none resize-none text-sm"
            />
            <div className="flex justify-between items-center mt-2">
              <span className="text-gray-600 text-xs">{weeklyNote.length}/1000</span>
              <div className="flex gap-2">
                <button onClick={() => { setWeeklyNote(castleData?.weekly_note || ''); setEditingNote(false); }} className="px-3 py-1.5 text-sm bg-gray-800 hover:bg-gray-700 rounded-lg transition">Cancel</button>
                <button onClick={saveWeeklyNote} disabled={savingNote} className="px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 rounded-lg transition">
                  {savingNote ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-300">
            {castleData?.weekly_note || <span className="text-gray-600 italic">No note yet. Add one to share with your supporters!</span>}
          </p>
        )}
      </div>

      {/* Stat Cards */}
      {castleData && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-[#1c1f2d] rounded-xl p-5 border border-gray-800/50">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-400 text-sm">Total Raised</p>
              <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                <FaDollarSign className="text-cyan-400" size={14} />
              </div>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(castleData.total_raised)}</p>
          </div>

          <div className="bg-[#1c1f2d] rounded-xl p-5 border border-gray-800/50">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-400 text-sm">Brickagy</p>
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <FaPercentage className="text-emerald-400" size={14} />
              </div>
            </div>
            <p className="text-2xl font-bold">{castleData.brickagy}%</p>
          </div>

          <div className="bg-[#1c1f2d] rounded-xl p-5 border border-gray-800/50">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-400 text-sm">Bricks Used</p>
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <FaCube className="text-blue-400" size={14} />
              </div>
            </div>
            <p className="text-2xl font-bold">{castleData.bricks_used}</p>
          </div>

          <div className="bg-[#1c1f2d] rounded-xl p-5 border border-gray-800/50">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-400 text-sm">Top Donor</p>
              <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <FaTrophy className="text-amber-400" size={14} />
              </div>
            </div>
            <p className="text-lg font-bold truncate">{castleData.top_donor_name}</p>
            <p className="text-xs text-gray-500">{formatCurrency(castleData.top_donor_amount)} {terms.donation}</p>
          </div>
        </div>
      )}

      {/* Castle Visualization */}
      {castleData && (
        <CastleVisualization bricks={castleData.bricks} goal={castleData.castle_goal} />
      )}

      {/* Recent Brick Purchases */}
      {castleData && castleData.bricks.length > 0 && (
        <div className="bg-[#1c1f2d] rounded-xl p-6 border border-gray-800/50 mt-6">
          <h3 className="font-semibold mb-1">Recent Brick Purchases</h3>
          <p className="text-xs text-gray-500 mb-4">Latest contributions to your castle</p>
          <div className="space-y-3">
            {[...castleData.bricks].reverse().slice(0, 10).map((brick, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-gray-800/50 last:border-0">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    brick.tier === 'title' ? 'bg-amber-500/10' :
                    brick.tier === 'premium' ? 'bg-purple-500/10' : 'bg-blue-500/10'
                  }`}>
                    <FaCube className={tierColor(brick.tier)} size={12} />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{brick.donor_name}</p>
                    <p className="text-xs text-gray-500">{brick.message || `${terms.Donation}`}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-semibold ${tierColor(brick.tier)}`}>{formatCurrency(brick.amount)}</p>
                  <p className="text-xs text-gray-500">{formatDate(brick.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
